import React, { useState, useCallback, useRef } from 'react';
import { useAuth, useUser } from '../contexts/TestAuthContext';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import api, { uploadFiles } from '../utils/api';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Loader2,
  FileSpreadsheet,
  Receipt,
  Building2,
  Landmark,
  Download,
  ArrowRight,
  Table,
  Database
} from 'lucide-react';
import ZipBatchUpload from '../components/wizard/ZipBatchUpload';
import { DocumentTypeIcon, DocumentTypeBadge, MessinessCheckModal, MessinessResult } from '../components/bulk-upload';
import { ENHANCED_TEMPLATE_CSV, FIELD_GUIDE_URL, downloadTemplate, downloadFieldGuide } from '../utils/bulkUploadTemplates';

interface DocumentResult {
  filename: string;
  status: string;
  document_type: string | null;
  confidence: number | null;
  year: number | null;
  extracted_data: any;
  error: string | null;
}

interface BulkUploadResponse {
  upload_id: string;
  total_files: number;
  successful: number;
  failed: number;
  by_type: Record<string, number>;
  documents: DocumentResult[];
  organized_data: Record<string, any[]>;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || ''; // Keep for any remaining direct references

// Upload mode types
type UploadMode = 'documents' | 'spreadsheet';

export default function BulkUpload() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState<BulkUploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Upload mode: documents (K1, 1065, P&L PDFs) or spreadsheet (CSV, Excel, QuickBooks)
  const [uploadMode, setUploadMode] = useState<UploadMode>('documents');
  
  // Spreadsheet parsing results
  const [spreadsheetData, setSpreadsheetData] = useState<any>(null);
  const [spreadsheetFile, setSpreadsheetFile] = useState<File | null>(null);
  const [isParsingSpreadsheet, setIsParsingSpreadsheet] = useState(false);
  
  // Pre-fill form state for Step 1 data
  const [clientInfo, setClientInfo] = useState({
    client_name: '',
    claimant_name: '',
    business_address: ''
  });
  
  // Track if we're adding more files to existing results
  const [isAppendMode, setIsAppendMode] = useState(false);
  const [previousResults, setPreviousResults] = useState<BulkUploadResponse | null>(null);
  
  // ZIP batch upload modal
  const [showZipUpload, setShowZipUpload] = useState(false);
  
  // Messiness check state - using imported MessinessResult type
  const [messinessResults, setMessinessResults] = useState<MessinessResult[]>([]);
  const [showMessinessModal, setShowMessinessModal] = useState(false);
  const [checkingMessiness, setCheckingMessiness] = useState(false);
  const [messinessCheckComplete, setMessinessCheckComplete] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);

  // Extract client info from results when they come in
  const extractClientInfo = useCallback((uploadResults: BulkUploadResponse) => {
    let clientName = '';
    let claimantName = '';
    let businessAddress = '';
    
    const organizedData = uploadResults.organized_data || {};
    
    console.log('📦 Extracting client info from organized_data:', organizedData);
    
    // Try to get partnership name and address from 1065 forms first
    if (organizedData['1065'] && organizedData['1065'].length > 0) {
      const form1065 = organizedData['1065'][0];
      console.log('📄 1065 data:', form1065);
      if (form1065.partnership_name) {
        clientName = form1065.partnership_name;
      }
      if (form1065.business_address) {
        businessAddress = form1065.business_address;
      }
    }
    
    // Always check K-1 for partner name (claimant)
    if (organizedData['K1'] && organizedData['K1'].length > 0) {
      const k1 = organizedData['K1'][0];
      console.log('📄 K1 data:', k1);
      // Use K1 partnership name as fallback for client name
      if (!clientName && k1.partnership_name) {
        clientName = k1.partnership_name;
      }
      // Always get partner name for claimant
      if (k1.partner_name) {
        claimantName = k1.partner_name;
      }
    }
    
    console.log('✅ Extracted client info:', { clientName, claimantName, businessAddress });
    
    setClientInfo({
      client_name: clientName,
      claimant_name: claimantName,
      business_address: businessAddress
    });
  }, []);

  // Check files for messiness before processing
  const checkFilesForMessiness = useCallback(async (filesToCheck: File[]) => {
    if (filesToCheck.length === 0) return [];
    
    setCheckingMessiness(true);
    const results: MessinessResult[] = [];
    
    try {
      for (const file of filesToCheck) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          
          // Use centralized API utility for file upload
          const data = await uploadFiles('/api/v1/cleanup/check-messiness', formData, { showErrorToast: false });
          results.push({
            ...data,
            filename: file.name
          });
        } catch (err) {
          console.error(`Messiness check failed for ${file.name}:`, err);
          // If check fails, assume file is OK
          results.push({
            needs_cleanup: false,
            confidence: 0.5,
            reasons: ['Unable to analyze file'],
            recommendation: 'review' as const,
            file_type: file.name.split('.').pop() || 'unknown',
            issues: [],
            details: {},
            filename: file.name
          });
        }
      }
    } finally {
      setCheckingMessiness(false);
    }
    
    return results;
  }, []);

  // Handle proceeding to cleanup - Option A: Upload files directly to cleanup
  const handleProceedToCleanup = useCallback(async () => {
    if (files.length === 0) return;
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Create FormData with all files
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      
      // Use centralized API utility for file upload
      const data = await uploadFiles('/api/v1/cleanup/upload', formData, { showErrorToast: true });
      
      // Store wizard context for return
      sessionStorage.setItem('wizard_context', JSON.stringify({
        from_wizard: true,
        return_to: '/wizard-v2',
        step: 3,
        source: 'bulk_upload'
      }));
      
      // Navigate directly to validation page
      if (data.job_id) {
        setShowMessinessModal(false);
        navigate(`/cleanup/validation/${data.job_id}`);
      } else {
        throw new Error('No job ID returned');
      }
    } catch (err: any) {
      console.error('Cleanup upload failed:', err);
      setError(err.message || 'Failed to upload files for cleanup');
    } finally {
      setIsUploading(false);
    }
  }, [files, getToken, navigate]);

  // Handle skipping cleanup (proceed anyway)
  const handleSkipCleanup = useCallback(() => {
    setShowMessinessModal(false);
    setMessinessCheckComplete(true);
    // Continue with normal upload
  }, []);

  // Download CSV template - using extracted utility
  const handleDownloadTemplate = () => downloadTemplate();

  // Download field guide - using extracted utility
  const handleDownloadFieldGuide = () => downloadFieldGuide();

  // Parse spreadsheet file (CSV, Excel, QuickBooks) - Always use backend API
  const handleSpreadsheetFile = async (file: File) => {
    setIsParsingSpreadsheet(true);
    setError(null);
    setSpreadsheetFile(file);
    
    try {
      const ext = file.name.split('.').pop()?.toLowerCase();
      
      // All files go through the backend API for consistent parsing
      const formData = new FormData();
      formData.append('file', file);
      
      // Use centralized API utility for file upload
      const parsed = await uploadFiles('/api/v1/financial-docs/parse-excel', formData, { showErrorToast: false });
      
      if (!parsed.success) {
        throw new Error(parsed.errors?.join(', ') || 'Failed to parse file');
      }
      
      setSpreadsheetData(parsed);
      
      // Auto-fill client info from metadata if available
      if (parsed.metadata) {
        const meta = parsed.metadata;
        if (meta.business_name || meta.claimant_name) {
          setClientInfo({
            client_name: meta.business_name || '',
            claimant_name: meta.claimant_name || '',
            business_address: [
              meta.street_address,
              meta.city,
              meta.state,
              meta.zip_code
            ].filter(Boolean).join(', ')
          });
        }
      }
      
      if (parsed.warnings?.length > 0) {
        console.log('Parse warnings:', parsed.warnings);
      }
      
    } catch (err: any) {
      console.error('Spreadsheet parse error:', err);
      setError(err.message || 'Failed to parse spreadsheet');
      setSpreadsheetData(null);
    } finally {
      setIsParsingSpreadsheet(false);
    }
  };

  // Use spreadsheet data in wizard
  const handleUseSpreadsheetData = () => {
    if (!spreadsheetData) return;
    
    const metadata = (spreadsheetData as any).metadata || {};
    
    // Build comprehensive data for wizard
    const dataToStore = {
      type: 'spreadsheet',
      parsed_data: spreadsheetData,
      filename: spreadsheetFile?.name,
      file_id: (spreadsheetData as any).file_id || null,  // GridFS file ID for linking
      // Client info - prefer user edits, fallback to metadata
      clientInfo: {
        client_name: clientInfo.client_name || metadata.business_name || '',
        claimant_name: clientInfo.claimant_name || metadata.claimant_name || '',
        business_address: clientInfo.business_address || [
          metadata.street_address,
          metadata.city,
          metadata.state,
          metadata.zip_code
        ].filter(Boolean).join(', ') || ''
      },
      // Additional metadata from CSV
      businessInfo: {
        business_type: metadata.business_type || '',
        ein_tax_id: metadata.ein_tax_id || '',
        state: metadata.state || '',
        city: metadata.city || '',
        zip_code: metadata.zip_code || '',
        industry: metadata.industry || '',
        employee_count: metadata.employee_count || '',
        square_footage: metadata.square_footage || '',
        years_in_business: metadata.years_in_business || ''
      },
      eventInfo: {
        event_type: metadata.event_type || '',
        event_start_date: metadata.event_start_date || '',
        event_end_date: metadata.event_end_date || '',
        fema_declaration: metadata.fema_declaration || ''
      },
      contactInfo: {
        contact_email: metadata.contact_email || '',
        contact_phone: metadata.contact_phone || ''
      },
      insuranceInfo: {
        insurance_company: metadata.insurance_company || '',
        policy_number: metadata.policy_number || ''
      }
    };
    
    sessionStorage.setItem('bulk_upload_data', JSON.stringify(dataToStore));
    console.log('💾 Stored spreadsheet data for wizard:', dataToStore);
    
    navigate('/wizard-v2');
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    
    if (uploadMode === 'spreadsheet') {
      // For spreadsheet mode, only accept one file
      const spreadsheetFile = droppedFiles.find(file => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        return ['csv', 'xlsx', 'xls', 'iif', 'qbo'].includes(ext || '');
      });
      if (spreadsheetFile) {
        handleSpreadsheetFile(spreadsheetFile);
      }
    } else {
      // Document mode - accept PDFs and Excel
      const validFiles = droppedFiles.filter(file => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        return ['pdf', 'xlsx', 'xls'].includes(ext || '');
      });
      setFiles(prev => [...prev, ...validFiles]);
    }
  }, [uploadMode]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      
      if (uploadMode === 'spreadsheet') {
        // For spreadsheet mode, only accept one file
        const spreadsheetFile = selectedFiles.find(file => {
          const ext = file.name.split('.').pop()?.toLowerCase();
          return ['csv', 'xlsx', 'xls', 'iif', 'qbo'].includes(ext || '');
        });
        if (spreadsheetFile) {
          handleSpreadsheetFile(spreadsheetFile);
        }
      } else {
        // Document mode
        const validFiles = selectedFiles.filter(file => {
          const ext = file.name.split('.').pop()?.toLowerCase();
          return ['pdf', 'xlsx', 'xls'].includes(ext || '');
        });
        setFiles(prev => [...prev, ...validFiles]);
      }
    }
  }, [uploadMode]);

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    
    // Step 1: Check for messiness if not already done
    if (!messinessCheckComplete) {
      setCheckingMessiness(true);
      const results = await checkFilesForMessiness(files);
      setMessinessResults(results);
      setCheckingMessiness(false);
      
      // Check if any files need cleanup
      const needsCleanup = results.some(r => r.recommendation === 'cleanup');
      const hasIssues = results.some(r => r.issues.length > 0);
      
      if (needsCleanup || hasIssues) {
        // Auto-route to BI Cleanup for bank statements - skip the modal
        console.log('📋 Files detected as bank statements - routing to BI Cleanup automatically');
        
        // Show brief notification and proceed directly to cleanup
        setUploadStatus('Routing to BI Cleanup for optimal processing...');
        
        // Small delay for user feedback, then proceed
        setTimeout(() => {
          handleProceedToCleanup();
        }, 500);
        return;
      }
      
      // All files are clean, continue with normal flow
      setMessinessCheckComplete(true);
    }
    
    // Step 2: Proceed with normal upload

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);
    
    // Don't clear results if we're appending
    if (!isAppendMode) {
      setResults(null);
    }

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      // Simulate progress (actual progress would need XHR)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Use centralized API utility for file upload
      const newData: BulkUploadResponse = await uploadFiles('/api/v1/documents/bulk-upload', formData, { showErrorToast: true });

      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Merge with previous results if in append mode
      let finalResults: BulkUploadResponse;
      if (isAppendMode && previousResults) {
        // Merge the results
        finalResults = {
          upload_id: newData.upload_id, // Use new upload ID
          total_files: previousResults.total_files + newData.total_files,
          successful: previousResults.successful + newData.successful,
          failed: previousResults.failed + newData.failed,
          documents: [...previousResults.documents, ...newData.documents],
          by_type: {
            ...previousResults.by_type,
            ...Object.fromEntries(
              Object.entries(newData.by_type || {}).map(([key, value]) => [
                key,
                ((previousResults.by_type || {})[key] || 0) + (value as number)
              ])
            )
          },
          organized_data: {
            ...previousResults.organized_data,
            ...Object.fromEntries(
              Object.entries(newData.organized_data || {}).map(([key, value]) => [
                key,
                [...((previousResults.organized_data || {})[key] || []), ...(value as any[])]
              ])
            )
          },
          // Only include summary if both have it
          ...(previousResults.summary && newData.summary ? {
            summary: {
              total_processed: (previousResults.summary?.total_processed || 0) + (newData.summary?.total_processed || 0),
              successful: (previousResults.summary?.successful || 0) + (newData.summary?.successful || 0),
              failed: (previousResults.summary?.failed || 0) + (newData.summary?.failed || 0)
            }
          } : {})
        };
        console.log('📦 Merged results:', finalResults);
      } else {
        finalResults = newData;
      }
      
      setResults(finalResults);
      setFiles([]);
      setIsAppendMode(false);
      setPreviousResults(null);
      
      // Extract client info from results
      extractClientInfo(finalResults);
      
    } catch (err: any) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const exportToJSON = () => {
    if (!results) return;
    const blob = new Blob([JSON.stringify(results.organized_data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extracted_data_${results.upload_id}.json`;
    a.click();
  };

  // Handle starting a new claim with extracted data
  const handleStartNewClaim = () => {
    if (!results) {
      navigate('/wizard-v2');
      return;
    }

    // Store extracted data in sessionStorage for the wizard to pick up
    const extractedData = {
      bulkUploadId: results.upload_id,
      organized_data: results.organized_data,
      documents: results.documents,
      by_type: results.by_type,
      // Include client info from the form
      clientInfo: {
        client_name: clientInfo.client_name,
        claimant_name: clientInfo.claimant_name,
        business_address: clientInfo.business_address
      }
    };
    
    sessionStorage.setItem('bulk_upload_data', JSON.stringify(extractedData));
    console.log('💾 Stored bulk upload data for wizard:', extractedData);
    
    // Navigate to wizard
    navigate('/wizard-v2');
  };

  return (
    <AppLayout>
      <div className="p-6">
      {/* Messiness Check Modal - Using extracted component */}
      {showMessinessModal && messinessResults.length > 0 && (
        <MessinessCheckModal
          results={messinessResults}
          isUploading={isUploading}
          onProceedToCleanup={handleProceedToCleanup}
          onSkipCleanup={handleSkipCleanup}
          onCancel={() => {
            setShowMessinessModal(false);
            setMessinessResults([]);
            setFiles([]);
          }}
        />
      )}
      
      {/* Checking Messiness Indicator */}
      {checkingMessiness && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg shadow-xl p-6 flex items-center gap-4">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            <div>
              <p className="font-medium text-gray-900">Analyzing documents...</p>
              <p className="text-sm text-gray-500">Checking data quality before upload</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload status notification */}
      {uploadStatus && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg shadow-xl p-6 flex items-center gap-4">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <div>
              <p className="font-medium text-gray-900">{uploadStatus}</p>
              <p className="text-sm text-gray-500">This will only take a moment</p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Bulk Document Upload</h1>
          <p className="text-gray-600 mt-2">
            Upload multiple documents at once or import financial data from spreadsheets.
          </p>
        </div>

        {/* Upload Mode Selector */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">CHOOSE UPLOAD METHOD</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => {
                setUploadMode('documents');
                setSpreadsheetData(null);
                setSpreadsheetFile(null);
              }}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                uploadMode === 'documents'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <FileText className={`w-8 h-8 ${uploadMode === 'documents' ? 'text-blue-600' : 'text-gray-400'}`} />
                <div>
                  <p className={`font-semibold ${uploadMode === 'documents' ? 'text-blue-900' : 'text-gray-700'}`}>
                    Tax Documents
                  </p>
                  <p className="text-sm text-gray-500">K-1, 1065, P&L, Bank Statements</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">PDF, XLSX files • Auto-classification</p>
            </button>
            
            <button
              onClick={() => {
                setUploadMode('spreadsheet');
                setFiles([]);
                setResults(null);
              }}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                uploadMode === 'spreadsheet'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Table className={`w-8 h-8 ${uploadMode === 'spreadsheet' ? 'text-green-600' : 'text-gray-400'}`} />
                <div>
                  <p className={`font-semibold ${uploadMode === 'spreadsheet' ? 'text-green-900' : 'text-gray-700'}`}>
                    Spreadsheet Data
                  </p>
                  <p className="text-sm text-gray-500">CSV, Excel, QuickBooks</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Direct financial data import</p>
            </button>
          </div>
        </div>

        {/* Spreadsheet Mode - Template Download & Upload */}
        {uploadMode === 'spreadsheet' && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
            {/* Template Download Section */}
            <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Download className="w-8 h-8 text-green-600 mt-1" />
                  <div>
                    <p className="font-semibold text-green-900">Enhanced Financial Template v2.0</p>
                    <p className="text-sm text-green-700 mt-1">
                      <strong>Minimum 3 years required</strong> (4+ years preferred)
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      Use any date range that covers your claim period. Adjust the Month column to your actual years.
                    </p>
                    <ul className="text-xs text-green-600 mt-2 space-y-1">
                      <li>• Revenue, COGS, detailed payroll breakdown</li>
                      <li>• Officer salaries (guaranteed payments)</li>
                      <li>• K-1 distributions & ownership percentages</li>
                      <li>• Operating expenses (Rent, Utilities, Marketing)</li>
                    </ul>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleDownloadTemplate}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Template
                  </button>
                  <button
                    onClick={handleDownloadFieldGuide}
                    className="px-4 py-2 bg-white text-green-700 border border-green-300 rounded-lg text-sm font-medium hover:bg-green-50 transition flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Field Guide
                  </button>
                </div>
              </div>
            </div>

            {/* Supported Formats */}
            <h3 className="text-sm font-semibold text-gray-700 mb-3">SUPPORTED FORMATS</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Table className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium text-gray-900 text-sm">CSV</p>
                  <p className="text-xs text-gray-500">.csv files</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-gray-900 text-sm">Excel</p>
                  <p className="text-xs text-gray-500">.xlsx, .xls</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Database className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium text-gray-900 text-sm">QuickBooks IIF</p>
                  <p className="text-xs text-gray-500">.iif exports</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Database className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-900 text-sm">QuickBooks QBO</p>
                  <p className="text-xs text-gray-500">.qbo exports</p>
                </div>
              </div>
            </div>

            {/* Spreadsheet Upload Area */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                isDragging 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isParsingSpreadsheet ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="w-12 h-12 text-green-500 animate-spin mb-3" />
                  <p className="text-gray-600">Parsing spreadsheet...</p>
                </div>
              ) : spreadsheetFile ? (
                <div className="flex flex-col items-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mb-3" />
                  <p className="font-medium text-gray-900">{spreadsheetFile.name}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {(spreadsheetFile.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    onClick={() => {
                      setSpreadsheetFile(null);
                      setSpreadsheetData(null);
                    }}
                    className="mt-3 text-sm text-red-600 hover:text-red-700"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <>
                  <Table className={`w-12 h-12 mx-auto mb-3 ${isDragging ? 'text-green-500' : 'text-gray-400'}`} />
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Drop your spreadsheet here
                  </p>
                  <p className="text-gray-500 mb-4">or</p>
                  <label className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg cursor-pointer hover:bg-green-700 transition">
                    Choose File
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,.iif,.qbo"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </label>
                </>
              )}
            </div>

            {/* Spreadsheet Parse Results */}
            {spreadsheetData && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">PARSED DATA PREVIEW</h4>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {(spreadsheetData as any).months?.length || 0}
                      </p>
                      <p className="text-sm text-gray-600">Months</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-indigo-600">
                        {(spreadsheetData as any).year_range?.years_count || 0}
                      </p>
                      <p className="text-sm text-gray-600">
                        Years
                        {(spreadsheetData as any).year_range?.start && (
                          <span className="block text-xs">
                            ({(spreadsheetData as any).year_range.start}-{(spreadsheetData as any).year_range.end})
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        ${((spreadsheetData as any).revenue?.reduce((a, b) => a + b, 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-sm text-gray-600">Total Revenue</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-600">
                        {Object.keys((spreadsheetData as any).expenses || {}).length}
                      </p>
                      <p className="text-sm text-gray-600">Expense Categories</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {(spreadsheetData as any).k1_data?.partner_a_ownership_pct || 0}%/{(spreadsheetData as any).k1_data?.partner_b_ownership_pct || 0}%
                      </p>
                      <p className="text-sm text-gray-600">K-1 Ownership</p>
                    </div>
                  </div>
                  
                  {/* Data Quality Indicator */}
                  {(spreadsheetData as any).year_range?.years_count && (
                    <div className={`mt-3 p-3 rounded-lg ${
                      (spreadsheetData as any).year_range.years_count >= 4 
                        ? 'bg-green-50 border border-green-200' 
                        : (spreadsheetData as any).year_range.years_count >= 3
                          ? 'bg-yellow-50 border border-yellow-200'
                          : 'bg-red-50 border border-red-200'
                    }`}>
                      <p className={`text-sm font-medium ${
                        (spreadsheetData as any).year_range.years_count >= 4 
                          ? 'text-green-800' 
                          : (spreadsheetData as any).year_range.years_count >= 3
                            ? 'text-yellow-800'
                            : 'text-red-800'
                      }`}>
                        {(spreadsheetData as any).year_range.years_count >= 4 
                          ? '✅ Excellent: 4+ years of data for robust analysis'
                          : (spreadsheetData as any).year_range.years_count >= 3
                            ? '⚠️ Acceptable: 3 years of data (4+ recommended)'
                            : '❌ Insufficient: Less than 3 years of data required'}
                      </p>
                    </div>
                  )}
                  
                  {/* Metadata from CSV */}
                  {(spreadsheetData as any).metadata?._fields_filled > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-semibold text-blue-800 mb-3">
                        📋 Business Information Detected ({(spreadsheetData as any).metadata._fields_filled} fields)
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {(spreadsheetData as any).metadata.business_name && (
                          <div>
                            <span className="text-blue-600 font-medium">Business:</span>{' '}
                            <span className="text-gray-800">{(spreadsheetData as any).metadata.business_name}</span>
                          </div>
                        )}
                        {(spreadsheetData as any).metadata.claimant_name && (
                          <div>
                            <span className="text-blue-600 font-medium">Claimant:</span>{' '}
                            <span className="text-gray-800">{(spreadsheetData as any).metadata.claimant_name}</span>
                          </div>
                        )}
                        {(spreadsheetData as any).metadata.business_type && (
                          <div>
                            <span className="text-blue-600 font-medium">Type:</span>{' '}
                            <span className="text-gray-800">{(spreadsheetData as any).metadata.business_type}</span>
                          </div>
                        )}
                        {((spreadsheetData as any).metadata.city || (spreadsheetData as any).metadata.state) && (
                          <div>
                            <span className="text-blue-600 font-medium">Location:</span>{' '}
                            <span className="text-gray-800">
                              {[(spreadsheetData as any).metadata.city, (spreadsheetData as any).metadata.state].filter(Boolean).join(', ')}
                            </span>
                          </div>
                        )}
                        {(spreadsheetData as any).metadata.event_type && (
                          <div>
                            <span className="text-blue-600 font-medium">Event:</span>{' '}
                            <span className="text-gray-800">{(spreadsheetData as any).metadata.event_type}</span>
                          </div>
                        )}
                        {(spreadsheetData as any).metadata.event_start_date && (
                          <div>
                            <span className="text-blue-600 font-medium">Event Date:</span>{' '}
                            <span className="text-gray-800">
                              {(spreadsheetData as any).metadata.event_start_date}
                              {(spreadsheetData as any).metadata.event_end_date && ` - ${(spreadsheetData as any).metadata.event_end_date}`}
                            </span>
                          </div>
                        )}
                        {(spreadsheetData as any).metadata.fema_declaration && (
                          <div>
                            <span className="text-blue-600 font-medium">FEMA #:</span>{' '}
                            <span className="text-gray-800">{(spreadsheetData as any).metadata.fema_declaration}</span>
                          </div>
                        )}
                        {(spreadsheetData as any).metadata.insurance_company && (
                          <div>
                            <span className="text-blue-600 font-medium">Insurance:</span>{' '}
                            <span className="text-gray-800">{(spreadsheetData as any).metadata.insurance_company}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* K-1 Data Summary */}
                  {(spreadsheetData as any).k1_data?.partner_a_distribution?.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-800 mb-2">K-1 Partnership Data Detected:</p>
                      <div className="grid grid-cols-2 gap-4 text-sm text-blue-700">
                        <div>
                          <span className="font-medium">Partner A Distributions:</span> $
                          {((spreadsheetData as any).k1_data?.partner_a_distribution?.reduce((a: number, b: number) => a + b, 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div>
                          <span className="font-medium">Partner B Distributions:</span> $
                          {((spreadsheetData as any).k1_data?.partner_b_distribution?.reduce((a: number, b: number) => a + b, 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Payroll Summary */}
                  {(spreadsheetData as any).payroll?.total?.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-800 mb-2">Payroll Breakdown:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm text-blue-700">
                        <div>
                          <span className="font-medium">Officer Salaries:</span> $
                          {(((spreadsheetData as any).payroll?.officer_salary_a?.reduce((a: number, b: number) => a + b, 0) || 0) +
                            ((spreadsheetData as any).payroll?.officer_salary_b?.reduce((a: number, b: number) => a + b, 0) || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div>
                          <span className="font-medium">Employee Wages:</span> $
                          {((spreadsheetData as any).payroll?.employee_wages?.reduce((a: number, b: number) => a + b, 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div>
                          <span className="font-medium">Total Payroll:</span> $
                          {((spreadsheetData as any).payroll?.total?.reduce((a: number, b: number) => a + b, 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {(spreadsheetData as any).warnings?.length > 0 && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm font-medium text-yellow-800 mb-1">Warnings:</p>
                      <ul className="text-sm text-yellow-700 list-disc list-inside">
                        {(spreadsheetData as any).warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Client Info Form - Auto-filled from CSV metadata */}
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    CLIENT INFORMATION
                    {(spreadsheetData as any).metadata?._fields_filled > 0 && (
                      <span className="ml-2 text-xs font-normal text-green-600">(Auto-filled from CSV)</span>
                    )}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client/Business Name</label>
                      <input
                        type="text"
                        value={clientInfo.client_name || (spreadsheetData as any).metadata?.business_name || ''}
                        onChange={(e) => setClientInfo({ ...clientInfo, client_name: e.target.value })}
                        placeholder="Acme Manufacturing LLC"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Claimant Name</label>
                      <input
                        type="text"
                        value={clientInfo.claimant_name || (spreadsheetData as any).metadata?.claimant_name || ''}
                        onChange={(e) => setClientInfo({ ...clientInfo, claimant_name: e.target.value })}
                        placeholder="John Smith"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
                      <input
                        type="text"
                        value={clientInfo.business_address || [
                          (spreadsheetData as any).metadata?.street_address,
                          (spreadsheetData as any).metadata?.city,
                          (spreadsheetData as any).metadata?.state,
                          (spreadsheetData as any).metadata?.zip_code
                        ].filter(Boolean).join(', ') || ''}
                        onChange={(e) => setClientInfo({ ...clientInfo, business_address: e.target.value })}
                        placeholder="123 Main St, Charlotte, NC 28202"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Use Data Button */}
                <button
                  onClick={handleUseSpreadsheetData}
                  className="mt-6 w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition flex items-center justify-center gap-2"
                >
                  Use This Data in Claim Wizard
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Parse Error</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ZIP Batch Upload Button */}
        {uploadMode === 'documents' && !results && (
          <div className="mb-6">
            <button
              onClick={() => setShowZipUpload(true)}
              className="w-full py-6 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-700 transition shadow-lg flex items-center justify-center gap-3"
            >
              <span className="text-3xl">📦</span>
              <div className="text-left">
                <div>Upload ZIP File - Process Up to 50 Documents</div>
                <div className="text-sm font-normal opacity-90">Drag one ZIP, we'll extract & categorize everything</div>
              </div>
            </button>
          </div>
        )}

        {/* Document Mode - Supported Types Info */}
        {uploadMode === 'documents' && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">SUPPORTED DOCUMENT TYPES</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <Receipt className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="font-medium text-blue-900">Schedule K-1</p>
                  <p className="text-xs text-blue-600">Partner's income share</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <Building2 className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="font-medium text-blue-900">Form 1065</p>
                  <p className="text-xs text-blue-600">Partnership return</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <FileSpreadsheet className="w-8 h-8 text-green-500" />
                <div>
                  <p className="font-medium text-green-900">P&L Statement</p>
                  <p className="text-xs text-green-600">Income & expenses</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <Landmark className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="font-medium text-amber-900">Bank Statement</p>
                  <p className="text-xs text-amber-600">Account activity</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Area - show when in document mode AND (no results OR in append mode) */}
        {uploadMode === 'documents' && (!results || isAppendMode) && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
            {/* Append mode banner */}
            {isAppendMode && previousResults && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">
                      {previousResults.total_files} files already processed
                    </p>
                    <p className="text-sm text-blue-600">
                      Add more files to include in this batch
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsAppendMode(false);
                    setPreviousResults(null);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            )}
            
            <div
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className={`w-16 h-16 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-700 mb-2">
                {isDragging ? 'Drop files here' : isAppendMode ? 'Add more files' : 'Drag & drop files here'}
              </p>
              <p className="text-gray-500 mb-4">or</p>
              <label className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition">
                Browse Files
                <input
                  type="file"
                  multiple
                  accept=".pdf,.xlsx,.xls,.csv"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </label>
              <p className="text-sm text-gray-400 mt-4">
                Supported: PDF, XLSX, XLS, CSV • Upload multiple files at once (max 50)
              </p>
              <p className="text-xs text-gray-400 mt-1">
                💡 Tip: Select all your bank statements (Jan-Dec) in one go for faster processing
              </p>
            </div>

            {/* Selected Files */}
            {files.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  SELECTED FILES ({files.length})
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">{file.name}</p>
                          <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 text-gray-400 hover:text-red-500 transition"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Upload Button */}
                <button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="mt-4 w-full py-3 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing... {uploadProgress}%
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Extract & Classify Documents
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Upload Failed</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {results && !isAppendMode && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Extraction Results</h2>
                <div className="flex gap-3">
                  <button
                    onClick={exportToJSON}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export JSON
                  </button>
                  <button
                    onClick={() => {
                      setPreviousResults(results);
                      setIsAppendMode(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Add More Files
                  </button>
                  <button
                    onClick={() => {
                      setResults(null);
                      setPreviousResults(null);
                      setIsAppendMode(false);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition"
                  >
                    Start Over
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-blue-600">{results.total_files}</p>
                  <p className="text-sm text-blue-800">Total Files</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-green-600">{results.successful}</p>
                  <p className="text-sm text-green-800">Successful</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-red-600">{results.failed}</p>
                  <p className="text-sm text-red-800">Failed</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-blue-600">{Object.keys(results.by_type).length}</p>
                  <p className="text-sm text-blue-800">Types Detected</p>
                </div>
              </div>

              {/* By Type Breakdown */}
              <div className="flex flex-wrap gap-2 mb-6">
                {Object.entries(results.by_type).map(([type, count]) => (
                  <div key={type} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                    <DocumentTypeIcon type={type} />
                    <span className="text-sm font-medium text-gray-700">{type}: {count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Document List */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Details</h3>
              <div className="space-y-3">
                {results.documents.map((doc, index) => (
                  <div key={index} className={`p-4 rounded-lg border ${doc.status === 'success' ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {doc.status === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{doc.filename}</p>
                          <div className="mt-1">
                            <DocumentTypeBadge type={doc.document_type} confidence={doc.confidence} />
                          </div>
                          {doc.year && (
                            <p className="text-sm text-gray-500 mt-1">Year: {doc.year}</p>
                          )}
                          {doc.error && (
                            <p className="text-sm text-red-600 mt-1">{doc.error}</p>
                          )}
                        </div>
                      </div>
                      <DocumentTypeIcon type={doc.document_type} />
                    </div>

                    {/* Extracted Data Preview */}
                    {doc.extracted_data && doc.status === 'success' && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 mb-2">EXTRACTED DATA</p>
                        <div className="text-sm text-gray-600 font-mono bg-white p-2 rounded border max-h-32 overflow-y-auto">
                          <pre>{JSON.stringify(doc.extracted_data, null, 2).slice(0, 500)}...</pre>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Client Information Form - Pre-fill for Step 1 */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
              <p className="text-sm text-gray-500 mb-4">
                This information will be pre-filled in Step 1 of the claim wizard. Edit as needed.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client/Business Name</label>
                  <input
                    type="text"
                    value={clientInfo.client_name}
                    onChange={(e) => setClientInfo({ ...clientInfo, client_name: e.target.value })}
                    placeholder="Acme Restaurant Group LLC"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {clientInfo.client_name && (
                    <p className="text-xs text-green-600 mt-1">✓ Auto-detected from documents</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Claimant Name</label>
                  <input
                    type="text"
                    value={clientInfo.claimant_name}
                    onChange={(e) => setClientInfo({ ...clientInfo, claimant_name: e.target.value })}
                    placeholder="John Smith"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {clientInfo.claimant_name && (
                    <p className="text-xs text-green-600 mt-1">✓ Auto-detected from K-1</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
                  <input
                    type="text"
                    value={clientInfo.business_address}
                    onChange={(e) => setClientInfo({ ...clientInfo, business_address: e.target.value })}
                    placeholder="123 Main St, New York, NY 10001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {clientInfo.business_address && (
                    <p className="text-xs text-green-600 mt-1">✓ Auto-detected from 1065</p>
                  )}
                </div>
              </div>
            </div>

            {/* Use in Wizard CTA */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold">Ready to use this data?</h3>
                  <p className="text-blue-100 mt-1">Import the extracted data directly into a new claim calculation</p>
                </div>
                <button 
                  onClick={handleStartNewClaim}
                  className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition flex items-center gap-2"
                >
                  Start New Claim
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* ZIP Upload Modal */}
      {showZipUpload && (
        <ZipBatchUpload
          onUploadComplete={(result) => {
            console.log('ZIP upload complete:', result);
            setShowZipUpload(false);
            // Could process result here if needed
          }}
          onClose={() => setShowZipUpload(false)}
        />
      )}
      </div>
    </AppLayout>
  );
}
